export interface StyleData {
  id: number
  name: string
  /** The tag's own photo on the static host, or null — the text-only tile (BE-137). */
  image: string | null
  code?: string
  nameEn?: string
  completionPercent: number
  isFullyCompleted: boolean
  remainingSwipes: number
  swipesForCompletion: number
}

export interface ColorData {
  id: number
  name: string
  hex: string
}

export interface BrandData {
  id: number
  name: string
  logo: string
}

export interface RecommendationLink {
  id: string
  title: string
  url: string
  isExternal: boolean
}

export interface MyStylePageData {
  completionPercent: number
  tasteConfigId: string
  description: string
  styles: StyleData[]
  favoriteColors: ColorData[]
  favoriteBrands: BrandData[]
  recommendations: {
    title: string
    subtitle: string
    icon?: string
    links: RecommendationLink[]
  }
}

export interface AvailableStyle {
  id: number
  name: string
  image: string | null
  code?: string
  nameEn?: string
  completionPercent: number
  isFullyCompleted: boolean
  remainingSwipes: number
  swipesForCompletion: number
}

export interface AvailableColor {
  id: number
  name: string
  hex: string
}

export interface AvailableBrand {
  id: number
  name: string
  logo: string
}

/** BE-139 — the taste survey's own values. `never` clears the height on the server. */
export type HeelFrequency = 'never' | 'sometimes' | 'often'
export type HeelHeight = 'up-to-4cm' | '5-7cm' | '8cm-plus'

export interface HeelPreferencePayload {
  frequency: HeelFrequency
  height?: HeelHeight | null
}

export interface HeelPreferenceResponse {
  frequency: string | null
  height: string | null
}

/**
 * `GET /user/style/dna` (§ 2.8) — the shopper's taste in one card. Camelised by the
 * interceptor. `summary` is ALWAYS null today (the AIRA narrative is not available);
 * every input with no data is an empty list; `updatedAt` is null before the first swipe.
 */
export interface UserStyleDnaStyle {
  styleId: number
  code: string
  name: string
  /** her preference normalised to her strongest style (0..1; 0 for a chosen style never swiped) */
  weight: number
  /** min(swipes, 30) / 30 * 100 */
  exploredPercent: number
  source: 'user' | 'photos' | string
  /** the model's confidence for a `photos` style; null for `user` */
  confidence: number | null
}

export interface UserStyleDna {
  styles: UserStyleDnaStyle[]
  colorsTop: { code: string; label: string; weight: number }[]
  brandsTop: { id: number; name: string; weight: number }[]
  /** `key` = `<tag type>.<tag code>` — category, fit, print, type, price_tier */
  attributesTop: { key: string; label: string; weight: number }[]
  /** the personalization confidence (0..1), the same number search personalises by */
  confidence: number
  summary: string | null
  updatedAt: string | null
}

/**
 * Inspiration photos (BE-140, § 3.3) under `/user/style/photos`. `photoId` (uuid) names a
 * PENDING upload and is consumed by the confirm; `id` (int) is the stored photo. Every
 * confirmed photo is `processing` with `reason: ml_style_inference_not_available` until the
 * inference service ships — the honest state, not a transient spinner.
 */
export type InspirationPhotoStatus = 'processing' | 'ready' | 'failed'

export interface InspirationPhoto {
  id: number
  /** presigned GET (1 h) or null */
  url: string | null
  status: InspirationPhotoStatus
  reason: string | null
  createdAt: string
}

export interface InspirationPhotosResponse {
  items: InspirationPhoto[]
  total: number
}

export interface InspirationPhotoUploadUrlResponse {
  photoId: string
  uploadUrl: string
  key: string
  expiresIn: number
}

/** The content types the upload-url accepts; anything else is a 400. */
export const INSPIRATION_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
/** `file_size` above this is a 400. */
export const INSPIRATION_PHOTO_MAX_BYTES = 5 * 1024 * 1024
